'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import type { UserRoleLevel } from '@/lib/supabase/services';

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles: UserRoleLevel[];
}

export default function RoleGuard({ children, allowedRoles }: RoleGuardProps) {
  const router = useRouter();
  const [status, setStatus] = useState<'loading' | 'authorized' | 'forbidden'>('loading');

  useEffect(() => {
    const supabase = createClient();

    const checkAccess = async () => {
      // 1. Verify active session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        router.replace('/');
        return;
      }

      // 2. Fetch user profile to get role
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', session.user.id)
        .single();

      if (error || !profile) {
        setStatus('forbidden');
        return;
      }

      if (allowedRoles.includes(profile.role as UserRoleLevel)) {
        setStatus('authorized');
      } else {
        setStatus('forbidden');
      }
    };

    checkAccess();
  }, [allowedRoles, router]);

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background" dir="rtl">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-text-secondary text-sm">جارٍ التحقق من الصلاحيات…</p>
        </div>
      </div>
    );
  }

  if (status === 'forbidden') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background" dir="rtl">
        <div className="bg-surface rounded-2xl shadow-lg p-10 flex flex-col items-center gap-5 max-w-sm w-full mx-4 text-center">
          {/* Shield icon */}
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-text-primary">غير مصرح بالوصول</h2>
          <p className="text-text-secondary text-sm leading-relaxed">
            هذه الصفحة متاحة فقط لـ <span className="font-semibold text-text-primary">المدير الإداري</span> و<span className="font-semibold text-text-primary">المدير التنفيذي</span>.
            <br />لا تملك الصلاحية الكافية للوصول إلى هذا القسم.
          </p>
          <button
            onClick={() => router.replace('/')}
            className="mt-2 px-6 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            العودة للرئيسية
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
